import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useTournament } from "@/lib/TournamentContext";
import { toast } from "sonner";
import { Lock, Trophy, Check } from "lucide-react";

const ROUNDS = ["r32", "r16", "qf", "sf", "final"];
const ROUND_LABEL = { r32: "Round of 32", r16: "Round of 16", qf: "Quarter-finals", sf: "Semi-finals", final: "Final" };
const SLOTS = { r32: 16, r16: 8, qf: 4, sf: 2, final: 1 };

// Drop any downstream pick that is no longer one of its two feeding winners.
function recalc(picks) {
  const out = { r32: { ...(picks.r32 || {}) }, r16: {}, qf: {}, sf: {}, final: {} };
  for (let ri = 1; ri < ROUNDS.length; ri++) {
    const rnd = ROUNDS[ri];
    const prev = out[ROUNDS[ri - 1]];
    const submitted = picks[rnd] || {};
    for (let s = 0; s < SLOTS[rnd]; s++) {
      const valid = [prev[2 * s], prev[2 * s + 1]].filter(Boolean);
      if (submitted[s] && valid.includes(submitted[s])) out[rnd][s] = submitted[s];
    }
  }
  return out;
}

const TeamBtn = ({ team, picked, correct, wrong, locked, onClick, testid }) => {
  if (!team) return (
    <div className="flex-1 flex items-center gap-1.5 px-2 py-1.5 text-zinc-600 text-xs border border-dashed border-zinc-800">
      <span className="w-4 h-4 inline-block border border-zinc-800 rounded-full" /> TBD
    </div>
  );
  return (
    <button
      data-testid={testid}
      disabled={locked}
      onClick={onClick}
      className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 text-xs uppercase tracking-wider border transition-colors disabled:cursor-default
        ${picked ? "bg-primary-accent/15 border-primary-accent text-white" : "border-zinc-800 text-zinc-300 hover:border-white/40"}
        ${correct ? "!border-green-500 !bg-green-500/15 text-green-300" : ""}
        ${wrong ? "opacity-50" : ""}`}
    >
      {team.crest && <img src={team.crest} alt="" className="w-4 h-4 object-contain flex-shrink-0" />}
      <span className="font-display truncate">{team.code || team.name}</span>
      {correct && <Check size={11} className="ml-auto text-green-400 flex-shrink-0" />}
    </button>
  );
};

export default function BracketKO() {
  const { active } = useTournament();
  const [struct, setStruct] = useState(null);
  const [picks, setPicks] = useState({ r32: {}, r16: {}, qf: {}, sf: {}, final: {} });
  const [meta, setMeta] = useState({ points: 0, breakdown: null, locked: false });
  const [loading, setLoading] = useState(true);
  const saveTimer = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, me] = await Promise.all([
        api.get(`/tournaments/${active.tournament_id}/bracket-ko/structure`),
        api.get(`/tournaments/${active.tournament_id}/bracket-ko/me`),
      ]);
      setStruct(s.data);
      setPicks(recalc(me.data.picks || {}));
      setMeta({ points: me.data.points, breakdown: me.data.points_breakdown, locked: me.data.locked || s.data.locked });
    } catch { setStruct(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (active) load(); /* eslint-disable-next-line */ }, [active?.tournament_id]);

  const teamById = useMemo(() => {
    const m = {};
    (struct?.r32 || []).forEach((t) => { if (t.home) m[t.home.id] = t.home; if (t.away) m[t.away.id] = t.away; });
    return m;
  }, [struct]);

  const winners = struct?.winners || { r32: [], r16: [], qf: [], sf: [], champion: null };

  const save = (next) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.post(`/tournaments/${active.tournament_id}/bracket-ko`, next);
        setMeta({ points: data.points, breakdown: data.points_breakdown, locked: data.locked });
      } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    }, 500);
  };

  const pick = (round, slot, teamId) => {
    if (meta.locked) return;
    const cur = picks[round]?.[slot];
    const next = recalc({ ...picks, [round]: { ...picks[round], [slot]: cur === teamId ? undefined : teamId } });
    setPicks(next);
    save(next);
  };

  // The two teams feeding a slot: R32 from fixtures, later rounds from prior picks.
  const slotTeams = (round, slot) => {
    if (round === "r32") {
      const seed = struct.r32[slot];
      return [seed?.home, seed?.away];
    }
    const prev = picks[ROUNDS[ROUNDS.indexOf(round) - 1]] || {};
    return [teamById[prev[2 * slot]] || null, teamById[prev[2 * slot + 1]] || null];
  };

  if (!active) return null;
  if (loading) return <div className="p-8 text-center text-zinc-500" data-testid="bracketko-loading">Loading bracket…</div>;
  if (!struct?.available) return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="bracketko-unavailable">
      <Trophy className="mx-auto mb-4 text-zinc-700" size={40} />
      <h2 className="font-display text-2xl uppercase mb-2">Bracket KO</h2>
      <p className="text-zinc-400 text-sm">{struct?.reason || "Round of 32 fixtures aren't set yet — check back once the knockout draw is confirmed."}</p>
    </div>
  );

  const filledR32 = Object.keys(picks.r32).length;
  const champPick = Object.values(picks.final)[0];

  return (
    <div className="max-w-[1400px] mx-auto px-3 md:px-6 py-6" data-testid="bracketko-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="label-tag text-zinc-500">Knockout Bracket</div>
          <h1 className="font-display text-3xl md:text-4xl uppercase">Bracket KO</h1>
          <p className="text-xs text-zinc-400 mt-1 max-w-xl">Pick the winner of every R32 tie — your bracket cascades through to the champion. R32 +{active.config?.pts_r32 ?? 2}, R16 +{active.config?.pts_r16 ?? 4}, QF +{active.config?.pts_qf ?? 8}, SF +{active.config?.pts_sf ?? 16}, Champion +{active.config?.pts_final ?? 32}.</p>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl text-primary-accent" data-testid="bracketko-points">{meta.points}</div>
          <div className="label-tag text-zinc-500">Points</div>
          {meta.locked
            ? <div className="text-[11px] uppercase tracking-widest text-primary-accent flex items-center gap-1 justify-end mt-1"><Lock size={11}/> Locked</div>
            : <div className="text-[11px] uppercase tracking-widest text-green-400 mt-1" data-testid="bracketko-progress">{filledR32}/16 R32 picked</div>}
        </div>
      </div>

      {champPick && teamById[champPick] && (
        <div className="surface-card p-4 mb-5 flex items-center gap-3" data-testid="bracketko-champion">
          <Trophy size={20} className="text-yellow-400" />
          <span className="label-tag text-zinc-500">Your champion</span>
          {teamById[champPick].crest && <img src={teamById[champPick].crest} className="w-6 h-6 object-contain" alt="" />}
          <span className="font-display text-xl uppercase">{teamById[champPick].name}</span>
          {winners.champion && (winners.champion === champPick
            ? <span className="ml-auto text-green-400 text-xs uppercase tracking-widest flex items-center gap-1"><Check size={12}/> Correct +{active.config?.pts_final ?? 32}</span>
            : <span className="ml-auto text-zinc-500 text-xs uppercase tracking-widest">Out</span>)}
        </div>
      )}

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {ROUNDS.map((round) => (
            <div key={round} className="w-56 flex-shrink-0" data-testid={`bracketko-col-${round}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="font-display text-sm uppercase tracking-wider">{ROUND_LABEL[round]}</span>
                {meta.breakdown && (
                  <span className="text-[10px] text-primary-accent font-display">+{round === "final" ? (meta.breakdown.champion || 0) : (meta.breakdown[round] || 0)}</span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {Array.from({ length: SLOTS[round] }).map((_, slot) => {
                  const [a, b] = slotTeams(round, slot);
                  const sel = picks[round]?.[slot];
                  const advanced = round === "final" ? (winners.champion ? [winners.champion] : []) : (winners[round] || []);
                  return (
                    <div key={slot} className="border border-white/5 bg-black/20 p-1.5 flex flex-col gap-1" data-testid={`bracketko-slot-${round}-${slot}`}>
                      {[a, b].map((tm, i) => (
                        <TeamBtn
                          key={i}
                          team={tm}
                          picked={sel && tm && sel === tm.id}
                          correct={sel && tm && sel === tm.id && advanced.includes(tm.id)}
                          wrong={sel && tm && sel === tm.id && advanced.length > 0 && !advanced.includes(tm.id)}
                          locked={meta.locked}
                          onClick={() => tm && pick(round, slot, tm.id)}
                          testid={`bracketko-pick-${round}-${slot}-${i}`}
                        />
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
